// P9.2 — OPTIONAL price-structure capability pack.
//
// Defines named price-structure concepts (liquidity sweep, displacement, fair-value gap,
// order block, breaker, dealing-range location, reference candle, structural break/reclaim,
// opposing-liquidity / measured-range targets) by COMPILING THEM DOWN to the generic
// event/region model. These names are pack vocabulary, NOT core ontology — the core engine
// runs identically without this pack loaded, and any setup that references
// 'price_structure:<concept>' without the pack raises UnsupportedCapabilityError.
//
// Computational library: structuralPrimitives.ts (versioned detectors with the confirmedAt
// no-lookahead contract). This pack owns those semantics; core does not.

import type { StructuralEvent } from '../structuralPrimitives';
import {
  dealingRangeAt, displacements, fairValueGaps, inverseFairValueGaps, liquiditySweeps,
  measuredRangeTarget, opposingLiquidityTarget, orderBlocks, breakers, rangeLocation, reclaims,
  structureBreaks, zoneMitigation,
} from '../structuralPrimitives';
import { firstSessionBar, inSessionWindow, TZ_LONDON, TZ_NEW_YORK, TZ_UTC, type TimezoneSpec } from '../multiTimeframe';
import type { CapabilityPack } from './registry';
import type { EventFeed } from '../setupStateMachine';

const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const str = (v: unknown, d: string): string => (typeof v === 'string' ? v : d);
const TZ: Record<string, TimezoneSpec> = { TZ_NEW_YORK, TZ_LONDON, TZ_UTC };

/** Precomputed-event feed: fires at each event's confirmedAt. */
const atFeed = (evs: StructuralEvent[]): EventFeed => {
  const byBar = new Map<number, StructuralEvent>();
  for (const e of evs) if (!byBar.has(e.confirmedAt)) byBar.set(e.confirmedAt, e);
  return (i) => byBar.get(i) ?? null;
};

export const PRICE_STRUCTURE_PACK: CapabilityPack = {
  id: 'price_structure',
  version: 1,
  concepts: {
    liquidity_sweep: {
      describe: 'a bar trades beyond a confirmed prior-extreme resting level and closes back (structuralPrimitives.liquiditySweeps)', version: 1,
      makeFeed: (bars, p) => atFeed(liquiditySweeps(bars, str(p.side, 'buyside') as 'buyside' | 'sellside', num(p.swingStrength, 2))),
    },
    displacement: {
      describe: 'single-candle body >= minBodyAtr × prior ATR in the stated direction', version: 1,
      makeFeed: (bars, p) => {
        const dir = str(p.direction, '');
        if (dir === 'with_prior_event') {
          return (i, ctx) => {
            const prior = [...ctx.consumed.values()].reverse().find((e) => e.direction !== 'neutral');
            if (!prior) return null;
            const d = prior.direction === 'bullish' ? 'bullish' : 'bearish';
            return displacements(bars.slice(0, i + 1), d, num(p.minBodyAtr, 1.0)).find((e) => e.confirmedAt === i) ?? null;
          };
        }
        return atFeed(displacements(bars, dir as 'bullish' | 'bearish', num(p.minBodyAtr, 1.5)));
      },
    },
    fair_value_gap: {
      describe: 'three-candle imbalance zone (bullish: c3 low > c1 high; bearish mirrored)', version: 1,
      makeFeed: (bars, p) => atFeed(fairValueGaps(bars, str(p.direction, 'bullish') as 'bullish' | 'bearish')),
    },
    inverse_fair_value_gap: {
      describe: 'an FVG closed fully through against its direction; inverts role', version: 1,
      makeFeed: (bars, p) => atFeed(inverseFairValueGaps(bars, str(p.direction, 'bullish') as 'bullish' | 'bearish')),
    },
    order_block: {
      describe: 'last opposite candle before a qualifying displacement (zone = its range)', version: 1,
      makeFeed: (bars, p) => atFeed(orderBlocks(bars, str(p.direction, 'bullish') as 'bullish' | 'bearish', num(p.minBodyAtr, 1.5))),
    },
    breaker: {
      describe: 'an order block later closed through against its direction; opposite zone on return', version: 1,
      makeFeed: (bars, p) => atFeed(breakers(bars, str(p.direction, 'bullish') as 'bullish' | 'bearish', num(p.minBodyAtr, 1.5))),
    },
    zone_mitigation: {
      describe: 'first touch of the most recent zoned event consumed by an earlier stage', version: 1,
      makeFeed: (bars) => (i, ctx) => {
        const src = [...ctx.consumed.values()].reverse().find((e) => e.zone);
        if (!src?.zone || i <= src.confirmedAt) return null;
        const hit = zoneMitigation(bars.slice(0, i + 1), src.zone, i);
        return hit && hit.refIndex === i ? hit : null;
      },
    },
    range_location: {
      describe: 'price location fraction within the most recent confirmed dealing range (bounds via params)', version: 1,
      makeFeed: (bars, p) => (i) => {
        const r = dealingRangeAt(bars, i, num(p.swingStrength, 2));
        if (!r) return null;
        const loc = rangeLocation(bars[i].close, r.zone!);
        return loc >= num(p.minFraction, 0) && loc <= num(p.maxFraction, 1) ? { ...r, meta: { loc } } : null;
      },
    },
    session_window: {
      describe: 'bar-open falls in an exchange-local clock window (deterministic DST)', version: 1,
      makeFeed: (bars, p) => {
        const tz = TZ[str(p.tz, 'TZ_UTC')] ?? TZ_UTC;
        const s = num(p.startMin, 0), e = num(p.endMin, 1440);
        return (i) => inSessionWindow(bars[i].timestamp, tz, s, e) ? { concept: 'session_window', version: 1, refIndex: i, confirmedAt: i, direction: 'neutral' } : null;
      },
    },
    reference_candle: {
      describe: 'first completed bar of a declared session window (deterministic reference)', version: 1,
      makeFeed: (bars, p) => {
        const tz = TZ[str(p.tz, 'TZ_UTC')] ?? TZ_UTC;
        const s = num(p.startMin, 0), e = num(p.endMin, 1440);
        return (i) => { const r = firstSessionBar(bars, tz, s, e, Math.max(0, i - 3)); return r === i ? { concept: 'reference_candle', version: 1, refIndex: i, confirmedAt: i, direction: 'neutral', level: bars[i].high, zone: [bars[i].low, bars[i].high] } : null; };
      },
    },
    candle_violation: {
      describe: 'a close beyond the high/low of the most recent consumed reference bar', version: 1,
      makeFeed: (bars, p) => (i, ctx) => {
        const src = [...ctx.consumed.values()].reverse().find((e) => e.refIndex >= 0);
        if (!src || i <= src.confirmedAt) return null;
        const side = str(p.side, 'low');
        const check = (s: 'high' | 'low') => {
          const level = s === 'high' ? bars[src.refIndex].high : bars[src.refIndex].low;
          const broke = s === 'high' ? bars[i].close > level : bars[i].close < level;
          return broke ? { concept: 'candle_violation' as const, version: 1, refIndex: src.refIndex, confirmedAt: i, direction: s === 'high' ? 'bullish' as const : 'bearish' as const, level } : null;
        };
        return side === 'either' ? (check('high') ?? check('low')) : check(side as 'high' | 'low');
      },
    },
    structure_break_of_consumed_level: {
      describe: 'a close beyond the first consumed event level in the stated direction', version: 1,
      makeFeed: (bars, p) => (i, ctx) => {
        const src = [...ctx.consumed.values()].find((e) => e.level !== undefined);
        if (!src || i <= src.confirmedAt) return null;
        const dir = str(p.direction, 'bullish');
        const broke = dir === 'bullish' ? bars[i].close > src.level! : bars[i].close < src.level!;
        return broke ? { concept: 'structure_break', version: 1, refIndex: i, confirmedAt: i, direction: dir as 'bullish' | 'bearish', level: src.level } : null;
      },
    },
    structure_break: {
      describe: 'a close beyond the most recent confirmed opposing swing point', version: 1,
      makeFeed: (bars, p) => atFeed(structureBreaks(bars, str(p.direction, 'bullish') as 'bullish' | 'bearish', num(p.swingStrength, 2))),
    },
    reclaim_of_consumed_level: {
      describe: 'after violating the consumed level, a close back within withinBars', version: 1,
      makeFeed: (bars, p) => (i, ctx) => {
        const src = [...ctx.consumed.values()].find((e) => e.level !== undefined);
        if (!src || i <= src.confirmedAt) return null;
        const r = reclaims(bars.slice(0, i + 1), src.level!, 'below', src.confirmedAt, num(p.withinBars, 12));
        return r && r.confirmedAt === i ? r : null;
      },
    },
    opposing_liquidity_target: {
      describe: 'nearest confirmed opposing resting-extreme level beyond entry (as-of entry)', version: 1,
      makeFeed: (bars, p, direction) => (i, ctx) => {
        // fires when the target computed at scenario completion is tagged — used post-entry
        const anchor = [...ctx.consumed.values()].pop();
        if (!anchor) return null;
        const t = opposingLiquidityTarget(bars, direction, anchor.confirmedAt, bars[anchor.confirmedAt].close, num(p.swingStrength, 2));
        if (!t) return null;
        const tagged = direction === 'long' ? bars[i].high >= t.level! : bars[i].low <= t.level!;
        return tagged ? { ...t, confirmedAt: i } : null;
      },
    },
    measured_range_target: {
      describe: 'breakout edge ± mult × most-recent dealing-range height', version: 1,
      makeFeed: (bars, p, direction) => (i, ctx) => {
        const anchor = [...ctx.consumed.values()].pop();
        if (!anchor) return null;
        const r = dealingRangeAt(bars, anchor.confirmedAt, 2);
        if (!r) return null;
        const level = measuredRangeTarget(r.zone!, direction, num(p.mult, 1)).level!;
        const tagged = direction === 'long' ? bars[i].high >= level : bars[i].low <= level;
        return tagged ? { concept: 'measured_range_target', version: 1, refIndex: i, confirmedAt: i, direction: direction === 'long' ? 'bullish' : 'bearish', level } : null;
      },
    },
  },
};
